<template>
  <div
    v-bind="$attrs"
    ref="promoBarRef"
    class="relative w-full flex center bg-brand-1 px-4 <lg:h-11 lg:h-9 lg:px-8"
    :style="{
      'background': activeColors?.background,
      '--c': activeColors?.textColor,
      '--ch': activeColors?.hoverLinkColor || activeColors?.textColor,
    }"
  >
    <client-only>
      <base-ticker
        v-if="displayedMessages.length"
        ref="tickerRef"
        :active="activeIndex"
        :interval="content.autorotateTime"
        :max="displayedMessages.length"
        @change="activeIndex = $event"
      >
        <transition-group name="fade">
          <div
            v-show="i === activeIndex"
            v-for="(message, i) in displayedMessages"
            :key="message.id"
            class="absolute-0 flex center gap-2 pl-4"
          >
            <cms-section
              v-if="!isMessage(message)"
              class="min-w-0 overflow-hidden text-center c-$c lh-1 [&_br]:hidden <lg:caption-1 lg:body-2 lg:fw-bold"
              :section="{ items: [message], name: 'promo-bar-message' }"
            />
            <cms-promo-bar-message
              v-else
              class="min-w-0 overflow-hidden text-center c-$c lh-1 [&_br]:hidden <lg:caption-1 lg:body-2 lg:fw-bold"
              :content="message"
            />

            <!-- Multiple messages: chevron opens panel with all -->
            <base-button
              v-if="displayedMessages.length > 1"
              :aria-label="$t.showAll"
              class="shrink-0 cursor-pointer p-2 c-$c duration hover:c-$ch"
              data-test-id="promo-bar-chevron"
              @click="openPromoBarDialog"
            >
              <vf-icon :dir="isDialogOpen ? 'up' : 'down'" name="chevron" size="md" />
            </base-button>

            <!-- Single message with expandedRichText: info icon opens panel -->
            <base-button
              v-else-if="hasAdditionalInfo"
              :aria-label="$t.promotionDetails"
              class="shrink-0 cursor-pointer c-$c duration hover:c-$ch"
              data-test-id="promo-bar-info"
              @click="DialogPromoBar.open({ content })"
            >
              <vf-icon name="info" size="md" />
            </base-button>
          </div>
        </transition-group>
      </base-ticker>
    </client-only>

    <!-- Left authorable links -->
    <cms-promo-bar-links v-if="content.utilityLinks?.length" :links="content.utilityLinks" />

    <!-- Utility strip -->
    <cms-promo-bar-utilities />
  </div>
  <dialog-promo-bar :content variant="dark" />
</template>

<script lang="ts" setup>
import type { BaseTicker } from '#components'
import type { Message, PromoBarContent } from '#types/components/cms/promo-bar'
import type { Segmentation } from '#types/components/cms/segmentation'

defineOptions({ inheritAttrs: false })

const { content } = defineProps<Props>()

const isMessage = (msg: Message | Segmentation): msg is Message => msg.type === 'VfCanvasPromoBarMessage'

export type Props = {
  content: PromoBarContent
}

const { messages = [] } = content

const { maxPromoMessages } = useAppConfig().components.cms.promoBar
const header = useHeaderStore()
const { $t } = useNuxtApp()
const emptySections = useState<Record<string, boolean>>('emptySections', () => ({}))
const promoBarColors = useState('promoBarColors', () => ({}))

const promoBarRef = toRef(header.refs, 'promoBar')

const activeIndex = ref(0)
const tickerRef = useTemplateRef<InstanceType<typeof BaseTicker>>('tickerRef')
const isDialogOpen = ref(false)

const hovered = useElementHover(promoBarRef)
const { focused } = useFocusWithin(promoBarRef)

const displayedMessages = computed(() =>
  messages.filter((message) => !emptySections.value[message.id]).slice(0, maxPromoMessages)
)

const activeColors = computed(() => {
  const msg = displayedMessages.value[activeIndex.value]
  if (!msg) return
  if ('background' in msg) return msg
  return promoBarColors.value[msg.id]
})

const hasAdditionalInfo = computed(() => {
  const msg = displayedMessages.value[activeIndex.value]
  if (!msg) return
  if (!('expandedRichText' in msg)) return
  return displayedMessages.value.length === 1 && !!msg.expandedRichText?.html
})

const DialogPromoBar = createPanel('promo-bar')

const openPromoBarDialog = () => {
  isDialogOpen.value = true
  DialogPromoBar.open({ content }).then(() => {
    isDialogOpen.value = false
  })
}

watch([focused, hovered], ([focused, hovered]) => {
  if (focused || hovered)
    tickerRef.value?.pause()
  else
    tickerRef.value?.resume()
})
</script>
